"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Header from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import { getAlvaras, getStatusLabel } from "@/lib/store";
import {
  AlertTriangle,
  Building2,
  Calendar,
  ChevronRight,
  Clock,
  Download,
  Eye,
  FileCheck,
  Filter,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  User,
} from "lucide-react";
import Link from "next/link";

export default function DashboardPage() {
  const { user } = useAuth();
  const router = useRouter();
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [busca, setBusca] = useState("");

  if (!user) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Acesso Necessário</h2>
          <p className="text-gray-500 mb-6">Faça login para acessar seus alvarás.</p>
          <button onClick={() => router.push("/login")} className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition">
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  const alvaras = getAlvaras();
  const filtrados = alvaras.filter((a) => {
    const matchStatus = filtroStatus === "todos" || a.status === filtroStatus;
    const matchBusca =
      busca === "" ||
      a.protocolo.toLowerCase().includes(busca.toLowerCase()) ||
      a.endereco.toLowerCase().includes(busca.toLowerCase()) ||
      a.requerente.toLowerCase().includes(busca.toLowerCase());
    return matchStatus && matchBusca;
  });

  const stats = {
    total: alvaras.length,
    emitidos: alvaras.filter((a) => a.status === "emitido").length,
    pendentes: alvaras.filter(
      (a) =>
        a.status === "aguardando_confirmacao" ||
        a.status === "aguardando_pagamento" ||
        a.status === "em_analise"
    ).length,
    cancelados: alvaras.filter((a) => a.status === "cancelado").length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Meus Alvarás</h1>
            <p className="text-sm text-gray-500 mt-1">
              Acompanhe seus processos de licenciamento
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              href="/solicitacao/emissao"
              className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition flex items-center gap-2"
            >
              <Plus size={18} />
              Nova Emissão
            </Link>
            <Link
              href="/solicitacao/renovacao"
              className="border border-gray-300 text-gray-600 px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Renovação
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total", value: stats.total, icon: Building2, color: "bg-blue-50 text-blue-600" },
            { label: "Emitidos", value: stats.emitidos, icon: FileCheck, color: "bg-green-50 text-green-600" },
            { label: "Pendentes", value: stats.pendentes, icon: Clock, color: "bg-amber-50 text-amber-600" },
            { label: "Cancelados", value: stats.cancelados, icon: AlertTriangle, color: "bg-red-50 text-red-600" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <stat.icon size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por protocolo, endereço ou requerente..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-400" />
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value="todos">Todos os Status</option>
                <option value="emitido">Emitidos</option>
                <option value="em_analise">Em Análise</option>
                <option value="aguardando_confirmacao">Aguardando Confirmação</option>
                <option value="aguardando_pagamento">Aguardando Pagamento</option>
                <option value="cancelado">Cancelados</option>
              </select>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="space-y-4">
          {filtrados.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Building2 size={48} className="text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">Nenhum alvará encontrado</h3>
              <p className="text-sm text-gray-400">Ajuste os filtros ou solicite um novo alvará.</p>
            </div>
          ) : (
            filtrados.map((alvara) => {
              const status = getStatusLabel(alvara.status);
              return (
                <div
                  key={alvara.id}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:border-primary/30 transition cursor-pointer"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm font-bold text-primary">
                          {alvara.protocolo}
                        </span>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${status.color}`}>
                          {status.label}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${alvara.tipo === "emissao" ? "bg-blue-50 text-blue-600" : "bg-purple-50 text-purple-600"}`}>
                          {alvara.tipo === "emissao" ? "Emissão" : "Renovação"}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} /> {alvara.endereco}
                        </span>
                        <span className="flex items-center gap-1">
                          <User size={14} /> {alvara.requerente}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} /> {new Date(alvara.dataCriacao).toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 text-gray-400 hover:text-primary hover:bg-primary-light rounded-lg transition" title="Visualizar">
                        <Eye size={18} />
                      </button>
                      {alvara.status === "emitido" && (
                        <button className="p-2 text-gray-400 hover:text-primary hover:bg-primary-light rounded-lg transition" title="Baixar">
                          <Download size={18} />
                        </button>
                      )}
                      <ChevronRight size={18} className="text-gray-300" />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
